import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { CompetitionHeader } from '../CompetitionHeader';
import { LineupImportStatus } from '../LineupImportStatus';
import { LineupItem } from '../LineupItem';
import { So5LeaderboardGroupWrapper } from '../So5LeaderboardGroupWrapper';
import { useHierarchiedLineups } from '../useHierarchiedLineups';
import type { ImportedLineups_so5LineupImport } from './__generated__/index.graphql';

type Props = {
  importedLineups: ImportedLineups_so5LineupImport[];
};

export const ImportedLineups = ({ importedLineups }: Props) => {
  const hierarchiedLineups = useHierarchiedLineups(importedLineups);
  return (
    <Vertical gap={2}>
      {hierarchiedLineups.map(({ competitions, so5LeaderboardGroup }) => (
        <So5LeaderboardGroupWrapper
          key={so5LeaderboardGroup.slug}
          so5LeaderboardGroupInterface={so5LeaderboardGroup}
        >
          {competitions?.map(({ mainRarityType, seasonality, lineups }) => (
            <Fragment
              key={`${so5LeaderboardGroup.slug}-${mainRarityType}-${seasonality}`}
            >
              <CompetitionHeader
                rarity={mainRarityType}
                seasonality={seasonality}
                count={lineups.length}
              />
              {lineups.map(({ id, so5Leaderboard, appearances, aasmState }) => (
                <LineupItem
                  key={id}
                  iconUrl={
                    so5Leaderboard?.divisionIconUrl ||
                    so5Leaderboard?.svgLogoUrl ||
                    so5Leaderboard?.iconUrl
                  }
                  rarity={so5Leaderboard?.mainRarityType}
                  appearances={appearances.map(a => ({
                    id: a.composeTeamObject.id,
                    index: a.index,
                    captain: a.captain,
                    playerDisplayName:
                      a.composeTeamObject.anyPlayer.displayName,
                    avatarPictureUrl:
                      a.composeTeamObject.anyPlayer.avatarPictureUrl,
                  }))}
                  status={<LineupImportStatus status={aasmState} />}
                />
              ))}
            </Fragment>
          ))}
        </So5LeaderboardGroupWrapper>
      ))}
    </Vertical>
  );
};

ImportedLineups.fragments = {
  so5LineupImport: gql`
    fragment ImportedLineups_so5LineupImport on So5LineupImport {
      id
      aasmState
      appearances {
        index
        captain
        composeTeamObject {
          id
          anyPlayer {
            slug
            avatarPictureUrl
            displayName
          }
        }
      }
      so5Leaderboard {
        slug
        mainRarityType
        seasonality
        svgLogoUrl
        iconUrl
        divisionIconUrl
        so5LeaderboardGroup {
          slug
          ...So5LeaderboardGroupWrapper_so5LeaderboardGroupInterface
        }
      }
      ...useHierarchiedLineups_so5LineupImport
    }
    ${So5LeaderboardGroupWrapper.fragments.so5LeaderboardGroupInterface}
    ${useHierarchiedLineups.fragments.so5LineupImport}
  ` as TypedDocumentNode<ImportedLineups_so5LineupImport>,
};
